import {Device} from '@twilio/voice-sdk';
import eventBus from './../event-bus';
import titleService from './../services/title-service';
import VoiceService from "../services/voice-service";

/**
 * @param {object} call
 * @return {object}
 */
function getConversation(call = null) {
  return _.chain(call)
    .get('customParameters')
    .thru(val => val instanceof Map ? val : new Map([]))
    .thru(val => val.get('conversation') || '{}')
    .thru(unescape)
    .thru(JSON.parse)
    .value();
}

/**
 * @return {boolean}
 */
function isHttps() {
  return _.get(location, 'protocol') === 'https:';
}

export const status = {
  notInitialized: 1,
  initialization: 2,
  initialized: 3,
  ready: 4,
  connectingOut: 5,
  connectedOut: 6,
  connectingIn: 7,
  connectedIn: 8,
  error: 9,
  offline: 10,
};

const hangupStatuses = [
  status.connectingOut,
  status.connectedOut,
  status.connectedIn,
];

const activeStatuses = [
  status.connectingOut,
  status.connectedOut,
  status.connectingIn,
  status.connectedIn,
];

const onlineStatuses = [
  status.ready,
  status.connectingOut,
  status.connectedOut,
  status.connectingIn,
  status.connectedIn,
];

let device = null;
let call = null;

export const voice = {
  namespaced: true,
  state: {
    status: status.notInitialized,
    token: config('voice.token'),
    isVoiceMailRequestActive: false,
  },

  mutations: {
    initialization(state, {}) {
      state.status = status.initialization;
      call = null;
    },

    initialized(state, {}) {
      state.status = status.initialized;
    },

    ready(state, {}) {
      state.status = status.ready;
    },

    connectingOut(state, {call: _call, conversation}) {
      state.status = status.connectingOut;
      call = _call;
      eventBus.$emit('voice-call-outbound', {conversation})
    },

    connectedOut(state, {}) {
      state.status = status.connectedOut;
    },

    connectingIn(state, {call: _call, data: {conversation}}) {
      call = _call;
      state.status = status.connectingIn;
      eventBus.$emit('voice-call-inbound', {conversation});
    },

    connectedIn(state, {}) {
      state.status = status.connectedIn;
    },

    finish(state, {}) {
      call && eventBus.$emit('voice-call-end', {});
      call = null;
      state.status = status.ready;
    },

    error(state, {}) {
      state.status = status.error;
      call = null;
      state.isVoiceMailRequestActive = false;
    },

    offline(state, {}) {
      state.status = status.offline;
      call = null;
      state.isVoiceMailRequestActive = false;
    },

    voiceMailSendingInitiated(state, {}) {
      state.isVoiceMailRequestActive = true;
    },

    voiceMailSendingDone(state, {}) {
      state.isVoiceMailRequestActive = false;
    },
  },

  actions: {
    async init({state, commit, getters}) {
      if (!getters.isNotInitialized || !state.token || !isHttps()) {
        return;
      }

      commit('initialization', {});

      window.onbeforeunload = () => {
        return getters.hasActiveConnection ? 'You have an active call' : null;
      };

      try {
        const options = {
          // logLevel: 1,
        };
        device = new Device(state.token, options);

        device.on(Device.EventName.Registered, function (device) {
          commit('ready', {});
        });

        device.on(Device.State.Unregistered, function (device) {
          commit('offline', {});
        });

        device.on('incoming', function (_call) {
          if (! getters.isStatusReady) {
            _call.ignore();

            return;
          }

          commit('connectingIn', {
            call: _call,
            data: {
              conversation: getConversation(_call)
            }
          });

          call.on('accept', call => {
            commit('connectedIn', {call});
          });

          call.on('reject', () => {
            commit('finish', {});
          });

          call.on('disconnect', call => {
            commit('finish', {call});
          });

          call.on('cancel', () => {
            commit('finish', {});
          });


          titleService.blinkWhile('Incoming call', () => getters.isStatusConnectingIn);
        });

        device.on('error', function (error) {
          !getters.isStatusError && commit('error', error);
        });

        commit('initialized', {});

        await device.register();
      } catch (err) {
        commit('error', {});
      }
    },

    async connect({commit, getters}, {conversation}) {
      if (! getters.canConnect) {
        return;
      }

      const instance = await device.connect({
        params: {ConversationId: conversation.id}
      });
      commit('connectingOut', {call: instance, conversation});

      call.on('accept', call => {
        commit('connectedOut', {call});
      });

      call.on('disconnect', call => {
        commit('finish', {call});
      });
    },

    accept({getters}, {}) {
      if (!getters.canAccept) {
        return;
      }
      call && call.accept();
    },

    hangup({getters}, {}) {
      if (!getters.canHangup) {
        return;
      }

      call && call.disconnect();
    },

    reject({getters}, {}) {
      if (!getters.canReject) {
        return;
      }

      call && call.reject();
    },

    ignore({getters}, {}) {
      if (!getters.canIgnore) {
        return;
      }

      call && call.ignore();
    },

    mail({commit, getters}, {audioId}) {
      const sid = _.get(call, 'parameters.CallSid');
      if (!sid) {
        return Promise.reject(false);
      }
      const service = new VoiceService();

      commit('voiceMailSendingInitiated', {});

      return service.mail(sid, audioId)
        .then(res => {
          commit('voiceMailSendingDone', {});

          return res;
        });
    }
  },

  getters: {
    isNotInitialized: state => state.status === status.notInitialized,
    isStatusConnectingOut: state => state.status === status.connectingOut,
    isStatusConnectingIn: state => state.status === status.connectingIn,
    isStatusReady: state => state.status === status.ready,
    isStatusError: state => state.status === status.error,
    isOnline: state => _.includes(onlineStatuses, state.status),
    canAccept: state => state.status === status.connectingIn,
    canConnect: state => state.status === status.ready,
    canHangup: state => _.includes(hangupStatuses, state.status),
    canReject: state => state.status === status.connectingIn,
    canIgnore: state => state.status === status.connectingIn,
    hasActiveConnection: state => _.includes(activeStatuses, state.status),
    isVoiceMailRequestActive: state => state.isVoiceMailRequestActive,
  }
};
